import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  Check,
  ChevronDown,
  Compass,
  Menu,
  ShieldCheck,
  Sparkles,
  Users,
  X,
} from "lucide-react";
import { publicSite } from "@/config/publicSite";
import "@/pages/Landing.css";

function ActionButton({ children, to, variant = "primary", className = "" }) {
  const navigate = useNavigate();
  return (
    <button className={`landing-button landing-button--${variant} ${className}`} onClick={() => navigate(to)}>
      {children}
    </button>
  );
}

function ProductPreview() {
  return (
    <div className="product-preview" aria-label="Preview of the Treasure Tome learning adventure">
      <div className="preview-topbar">
        <div className="preview-avatar">🦜</div>
        <div>
          <span className="preview-eyebrow">TODAY'S VOYAGE</span>
          <strong>Keep your streak sailing!</strong>
        </div>
        <div className="preview-coins" aria-label="128 treasure coins">🪙 128</div>
      </div>
      <div className="preview-map">
        <span className="preview-cloud preview-cloud--one">☁️</span>
        <span className="preview-cloud preview-cloud--two">☁️</span>
        <div className="preview-route" />
        <div className="preview-island preview-island--done"><span>✓</span><small>Read</small></div>
        <div className="preview-island preview-island--active"><span>📖</span><small>Practice</small></div>
        <div className="preview-island preview-island--locked"><span>🏝️</span><small>Recite</small></div>
        <div className="preview-ship">⛵</div>
      </div>
      <div className="preview-card">
        <div><span>Current quest</span><strong>Practice for 5 minutes</strong></div>
        <div className="preview-progress"><i /></div>
        <b>3 / 5 min</b>
      </div>
    </div>
  );
}

function PlanCard({ plan }) {
  return (
    <article className={`plan-card ${plan.featured ? "plan-card--featured" : ""}`}>
      {plan.badge && <div className="plan-badge">{plan.badge}</div>}
      <div className="plan-icon">{plan.id === "family" ? <Users /> : <Compass />}</div>
      <p className="plan-audience">{plan.audience}</p>
      <h3>{plan.name}</h3>
      <div className="plan-price">
        {plan.regularPrice && <del>{plan.regularPrice}</del>}
        <strong>{plan.price}</strong>
        <span>{plan.period}</span>
      </div>
      <p className="plan-description">{plan.description}</p>
      <ul>{plan.features.map(feature => <li key={feature}><Check aria-hidden="true" />{feature}</li>)}</ul>
      <ActionButton to={`${publicSite.routes.getStarted}?plan=${plan.id}`} variant={plan.featured ? "gold" : "light"}>Start free <ArrowRight /></ActionButton>
    </article>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const goTo = id => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="landing-page">
      <header className="landing-nav">
        <button className="landing-brand" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Treasure Tome home">
          <span className="brand-mark">📖</span><span>Treasure Tome</span>
        </button>
        <nav className={menuOpen ? "landing-links landing-links--open" : "landing-links"} aria-label="Main navigation">
          <button onClick={() => goTo("how-it-works")}>How it works</button>
          <button onClick={() => goTo("features")}>Features</button>
          <button onClick={() => goTo("pricing")}>Pricing</button>
          <button onClick={() => goTo("faq")}>FAQ</button>
          <button className="nav-signin" onClick={() => navigate(publicSite.routes.signIn)}>Sign in</button>
          <ActionButton to={publicSite.routes.getStarted} variant="gold">Start free</ActionButton>
        </nav>
        <button className="menu-button" onClick={() => setMenuOpen(value => !value)} aria-expanded={menuOpen} aria-label="Toggle menu">
          {menuOpen ? <X /> : <Menu />}
        </button>
      </header>

      <main>
        <section className="hero-section">
          <div className="hero-copy">
            <div className="hero-kicker"><Sparkles /> Made for homeschool memory work</div>
            <h1>Turn memory work into an <em>adventure.</em></h1>
            <p>{publicSite.brand.description}</p>
            <div className="hero-actions">
              <ActionButton to={publicSite.routes.getStarted} variant="gold">Start your free voyage <ArrowRight /></ActionButton>
              <button className="text-action" onClick={() => goTo("how-it-works")}><span>See how it works</span></button>
            </div>
            <div className="trial-note"><ShieldCheck /><span><strong>{publicSite.trial.days} days free</strong> · No card required · Nothing renews automatically</span></div>
          </div>
          <div className="hero-visual">
            <div className="visual-sun" />
            <span className="visual-bird">〰</span>
            <ProductPreview />
          </div>
        </section>

        <section className="trust-strip" aria-label="Purchase promises">
          {publicSite.trustPoints.map(point => <div key={point}><Check />{point}</div>)}
        </section>

        <section className="landing-section" id="how-it-works">
          <div className="section-heading">
            <span className="section-label">HOW IT WORKS</span>
            <h2>A little practice. A lot of progress.</h2>
            <p>Treasure Tome turns consistent memory work into a journey learners can understand and enjoy.</p>
          </div>
          <div className="steps-grid">
            <article><span className="step-number">1</span><div className="step-illustration">📚</div><h3>Choose a piece</h3><p>Pick what to work on and set out on a new voyage.</p></article>
            <article><span className="step-number">2</span><div className="step-illustration">🧠</div><h3>Practice your way</h3><p>Use simple, focused tools that make repetition less overwhelming.</p></article>
            <article><span className="step-number">3</span><div className="step-illustration">🏆</div><h3>Watch progress grow</h3><p>Build a streak, complete quests, and earn treasure along the way.</p></article>
          </div>
        </section>

        <section className="feature-section" id="features">
          <div className="feature-intro">
            <span className="section-label">WHY FAMILIES USE IT</span>
            <h2>Practice that feels less like a chore.</h2>
            <p>The goal is not more screen time. It is a friendlier path through the focused practice homeschoolers are already doing.</p>
            <div className="parent-note"><Users /><div><strong>One family, separate adventures</strong><span>Parents can see progress while each child keeps an individual profile, character, and rewards.</span></div></div>
          </div>
          <div className="feature-grid">
            {publicSite.features.map(feature => <article key={feature.title}><span>{feature.icon}</span><h3>{feature.title}</h3><p>{feature.text}</p></article>)}
          </div>
        </section>

        <section className="landing-section pricing-section" id="pricing">
          <div className="section-heading">
            <span className="section-label">SIMPLE PRICING</span>
            <h2>Pay once. Practice all year.</h2>
            <p>No monthly payment and no subscription to remember to cancel.</p>
          </div>
          <div className="plans-grid">{publicSite.plans.map(plan => <PlanCard plan={plan} key={plan.id} />)}</div>
          <p className="pricing-footnote">Every new account starts with a {publicSite.trial.days}-day free trial. {publicSite.trial.cardRequired ? "A payment method is required." : "No payment method is required."}</p>
        </section>

        <section className="faq-section" id="faq">
          <div className="faq-heading"><span className="section-label">QUESTIONS, ANSWERED</span><h2>Good to know before you sail.</h2><p>Need something else? Email <a href={`mailto:${publicSite.contact.supportEmail}`}>{publicSite.contact.supportEmail}</a>.</p></div>
          <div className="faq-list">
            {publicSite.faqs.map((faq, index) => {
              const isOpen = openFaq === index;
              return <article className={isOpen ? "faq-item faq-item--open" : "faq-item"} key={faq.question}>
                <button onClick={() => setOpenFaq(isOpen ? -1 : index)} aria-expanded={isOpen}><span>{faq.question}</span><ChevronDown /></button>
                {isOpen && <p>{faq.answer}</p>}
              </article>;
            })}
          </div>
        </section>

        <section className="final-cta">
          <span className="cta-stars">✦ &nbsp; ✦ &nbsp; ✦</span>
          <BookOpen />
          <h2>Ready to begin the adventure?</h2>
          <p>Try every feature free for {publicSite.trial.days} days. No card, no automatic renewal, and no pressure.</p>
          <ActionButton to={publicSite.routes.getStarted} variant="gold">Start free today <ArrowRight /></ActionButton>
        </section>
      </main>

      <footer className="landing-footer">
        <div><div className="landing-brand"><span className="brand-mark">📖</span><span>Treasure Tome</span></div><p>{publicSite.brand.tagline}</p></div>
        <div className="footer-links"><button onClick={() => navigate(publicSite.routes.privacy)}>Privacy</button><button onClick={() => navigate(publicSite.routes.terms)}>Terms</button><a href={`mailto:${publicSite.contact.supportEmail}`}>Support</a><button onClick={() => navigate(publicSite.routes.signIn)}>Sign in</button></div>
        <p className="copyright">© {new Date().getFullYear()} Treasure Tome. All rights reserved.</p>
      </footer>
    </div>
  );
}

